<template>
  <div>
    <!-- Floating Logo Button (conditionally shown) -->
    <div v-if="showFloatingButton" class="floating-logo fixed top-4 left-1/2 -translate-x-1/2 z-40">
      <div
        class="logo-container cursor-pointer relative"
        @click="isSettingsOpen = true"
        title="Settings"
      >
        <img 
          :src="worldsSvg" 
          alt="" 
          class="worlds-layer absolute inset-0 w-full h-full"
        />
        <img 
          :src="veilSvg" 
          alt="Veil into Worlds" 
          class="veil-layer relative w-full h-full"
        />
      </div>
    </div>

    <!-- Settings Modal -->
    <Dialog v-model:open="isSettingsOpen">
      <DialogContent class="sm:max-w-[520px] max-h-[85vh] p-0 overflow-hidden">
        <!-- Header with branding -->
        <div class="px-6 pt-6 pb-4 border-b bg-muted/30">
          <DialogHeader>
            <DialogTitle class="flex items-center gap-3">
              <ViWLogo size="sm" />
              <span>Veil into Worlds</span>
            </DialogTitle>
            <DialogDescription class="sr-only">
              Application settings and preferences
            </DialogDescription>
          </DialogHeader>
        </div>

        <!-- Tab Navigation -->
        <Tabs v-model="activeTab" class="flex flex-col flex-1 min-h-0">
          <TabsList class="grid w-full grid-cols-4 rounded-none border-b bg-transparent h-12 px-2">
            <TabsTrigger 
              value="account" 
              class="flex items-center gap-1.5 data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-md"
            >
              <User class="h-4 w-4" />
              <span class="hidden sm:inline">Account</span>
            </TabsTrigger>
            <TabsTrigger 
              value="appearance" 
              class="flex items-center gap-1.5 data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-md"
            >
              <Palette class="h-4 w-4" />
              <span class="hidden sm:inline">Theme</span>
            </TabsTrigger>
            <TabsTrigger 
              value="integrations" 
              class="flex items-center gap-1.5 data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-md"
            >
              <Plug class="h-4 w-4" />
              <span class="hidden sm:inline">Connect</span>
            </TabsTrigger>
            <TabsTrigger 
              value="data" 
              class="flex items-center gap-1.5 data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-md"
            >
              <HardDrive class="h-4 w-4" />
              <span class="hidden sm:inline">Data</span>
            </TabsTrigger>
          </TabsList>
          
          <div class="flex-1 overflow-y-auto">
            <TabsContent value="account" class="m-0 p-6 data-[state=inactive]:hidden">
              <SettingsAccountTab />
            </TabsContent>
            
            <TabsContent value="appearance" class="m-0 p-6 data-[state=inactive]:hidden">
              <SettingsAppearanceTab />
            </TabsContent>
            
            <TabsContent value="integrations" class="m-0 p-6 data-[state=inactive]:hidden">
              <SettingsIntegrationsTab />
            </TabsContent>
            
            <TabsContent value="data" class="m-0 p-6 data-[state=inactive]:hidden">
              <SettingsDataTab />
            </TabsContent>
          </div>
        </Tabs>

        <!-- Footer Actions -->
        <div class="px-6 py-4 border-t bg-muted/30 flex gap-2">
          <Button variant="outline" size="sm" class="flex-1" @click="openFeedbackForm">
            <MessageSquareWarning class="h-4 w-4 mr-2" />
            Report Bug
          </Button>
          <Button v-if="Platform.isDesktop()" variant="outline" size="sm" class="flex-1" @click="openDevTools">
            <Bug class="h-4 w-4 mr-2" />
            DevTools
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { User, HardDrive, Bug, MessageSquareWarning, Palette, Plug } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import ViWLogo from '@/components/ViWLogo.vue'
import veilSvg from '@/assets/Veil.svg'
import worldsSvg from '@/assets/into-Worlds.svg'
import {
  SettingsAppearanceTab,
  SettingsIntegrationsTab,
  SettingsAccountTab,
  SettingsDataTab
} from '@/components/settings'
import { Platform } from '@/lib/platform'
import { invoke } from '@tauri-apps/api/core'
import * as Sentry from '@sentry/vue'

const activeTab = ref('account')

const openDevTools = async () => {
  if (Platform.isDesktop()) {
    await invoke('open_devtools')
  }
}

const openFeedbackForm = () => {
  // Get the feedback integration and show the form
  const feedback = Sentry.getFeedback()
  if (feedback) {
    feedback.createForm().then(form => {
      form.appendToDom()
      form.open()
    })
  }
}

const props = withDefaults(defineProps<{
  showFloatingButton?: boolean
  modelValue?: boolean
}>(), {
  showFloatingButton: true,
  modelValue: undefined
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const isSettingsOpen = ref(false)

watch(() => props.modelValue, (val) => {
  if (val !== undefined) {
    isSettingsOpen.value = val
  }
})

watch(isSettingsOpen, (val) => {
  emit('update:modelValue', val)
})

const openSettings = () => {
  isSettingsOpen.value = true
}

defineExpose({ openSettings })
</script>

<style scoped>
.logo-container {
  width: 64px;
  height: 64px;
  filter: drop-shadow(0 0 12px rgba(212, 175, 55, 0.5));
  transition: filter 200ms ease, transform 200ms ease;
}

.logo-container:hover {
  filter: drop-shadow(0 0 20px rgba(212, 175, 55, 0.8));
  transform: scale(1.05);
}

.worlds-layer {
  animation: slow-spin 60s linear infinite;
  object-fit: contain;
}

.veil-layer {
  object-fit: contain;
}

@keyframes slow-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
